import { Checkbox, Icon, IconButton, Tooltip } from '@smartgift/frontend-toolkit';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EDITABLE_TIMELINE_STATUS } from '@hero/constants';
import { OrderStatusBadge, Table } from '@hero/molecules';

import { RecipientSelectionHead } from '../Contacts';

import * as S from './style';

function OrderTable({
  data = [],
  globalFilters,
  setPagination,
  setGlobalFilters,
  pagination,
  pageCount,
  isLoading,
  renderNameCell,
  actionButtons,
  filterOptions,
  onRemoveSelectedRows,
  disabledRows = {},
  selectedRows,
  onChangeSelectedRows,
  customState = {},
  components = {}
}) {
  const { t } = useTranslation();
  const [rowSelection, setRowSelection] = useState(selectedRows || {});

  const columns = React.useMemo(
    () => [
      ...(actionButtons?.delete
        ? [
            {
              id: 'select',
              maxSize: 30,
              header: ({ table }) => {
                return (
                  <Checkbox
                    {...{
                      key: table.getState().pagination?.pageIndex,
                      checked: table.getIsAllPageRowsSelected(),
                      indeterminate: table.getIsSomePageRowsSelected(),
                      onChange: table.getToggleAllPageRowsSelectedHandler()
                    }}
                  />
                );
              },
              cell: ({ row }) => {
                const isSelectable = EDITABLE_TIMELINE_STATUS.includes(row?.original?.giftStatus);
                return (
                  <Checkbox
                    {...{
                      checked: isSelectable ? row.getIsSelected() : false,
                      disabled: !isSelectable,
                      indeterminate: row.getIsSomeSelected(),
                      onChange: row.getToggleSelectedHandler()
                    }}
                  />
                );
              }
            }
          ]
        : []),
      {
        header: t('name'),
        accessorFn: originalRow => {
          return {
            recipient: originalRow.recipient,
            code: originalRow.code
          };
        },
        cell: renderNameCell
      },
      {
        header: t('email'),
        accessorFn: originalRow => {
          return originalRow.recipient?.email;
        },
        cell: ({ cell }) => {
          return <S.Email>{cell.getValue()}</S.Email>;
        }
      },
      {
        header: t('status'),
        accessorFn: originalRow => ({
          status: originalRow.status,
          giftStatus: originalRow.giftStatus
        }),
        cell: ({ cell: { getValue } }) => {
          return <OrderStatusBadge status={getValue().giftStatus} />;
        }
      },
      ...(actionButtons?.delete
        ? [
            {
              id: 'actions',
              header: '',
              size: 50,
              cell: ({
                row: {
                  original: { recipient, giftStatus }
                }
              }) => {
                const isRemovable = EDITABLE_TIMELINE_STATUS.includes(giftStatus);
                return (
                  <Tooltip
                    content={t('recipientCannotBeDeletedInfo')}
                    tooltipConfig={{ placement: 'top' }}
                    active={!isRemovable}
                  >
                    <IconButton
                      variant="text"
                      color="fg"
                      onClick={() =>
                        actionButtons.delete.onClick(recipient, () => {
                          setRowSelection({});
                        })
                      }
                      disabled={!isRemovable}
                    >
                      <Icon name="delete" size="md" />
                    </IconButton>
                  </Tooltip>
                );
              }
            }
          ]
        : [])
    ],
    []
  );

  React.useEffect(() => {
    if (onChangeSelectedRows) onChangeSelectedRows(rowSelection);
  }, [rowSelection]);

  return (
    <S.Wrapper>
      <Table
        state={{
          disabledRows,
          onRemoveSelectedRows: () => {
            onRemoveSelectedRows(() => {
              setRowSelection({});
            });
          },
          ...customState
        }}
        data={data}
        columns={columns}
        manualPagination
        globalFilters={globalFilters}
        setGlobalFilters={setGlobalFilters}
        setPagination={setPagination}
        pagination={pagination}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        loading={isLoading}
        rowHeight={52}
        pageCount={pageCount}
        enabled={{ filter: true, header: true }}
        filterOptions={filterOptions}
        filterProps={{
          placeholder: t('filterByType', { type: t('status') }),
          searchPlaceholder: t('searchTagOnFilterPlaceholder'),
          hideSearchOnMenu: true,
          hasMenuSearch: false
        }}
        enableRowSelection={row => {
          return EDITABLE_TIMELINE_STATUS.includes(row?.original?.giftStatus);
        }}
        components={{
          SelectionHead: RecipientSelectionHead,
          ...components
        }}
        emptyTableContent={{
          hasDescription: true,
          description: t('pleaseTryDifferentFilter')
        }}
      />
    </S.Wrapper>
  );
}

OrderTable.propTypes = {
  data: PropTypes.array,
  globalFilters: PropTypes.any,
  setPagination: PropTypes.func,
  setGlobalFilters: PropTypes.func,
  pagination: PropTypes.any,
  pageCount: PropTypes.number,
  isLoading: PropTypes.bool,
  renderNameCell: PropTypes.any,
  actionButtons: PropTypes.shape({ delete: PropTypes.shape({ onClick: PropTypes.func }) }),
  filterOptions: PropTypes.array,
  onRemoveSelectedRows: PropTypes.func,
  disabledRows: PropTypes.any,
  selectedRows: PropTypes.any,
  onChangeSelectedRows: PropTypes.func,
  customState: PropTypes.any,
  components: PropTypes.any
};

export { OrderTable };
